// Chemin: C:\gestion-copro\src\pages\admin\ExportDonneesCSPage.tsx
// Session 213 : Export données RGPD — côté CS (principe ESCALATOR, réutilise export.service)

import React, { useState } from 'react';
import {
  Download, Shield, FileArchive, AlertCircle, CheckCircle,
  Clock, FileText, Mail, MailOpen, AlertTriangle, Users,
  CalendarDays, MessageSquare, File
} from 'lucide-react';
import { lancerExportDonnees, telechargerExport, formaterTailleFichier } from '../../services/export.service';

type ExportStatus = 'idle' | 'loading' | 'success' | 'error' | 'rate-limited';

interface ExportStats {
  documents: number;
  courriersEntrants: number;
  courriersSortants: number;
  signalements: number;
  dossiers: number;
  contacts: number;
  echeances: number;
  conversations: number;
  fichiers: number;
}

const ExportDonneesCSPage: React.FC = () => {
  const [status, setStatus] = useState<ExportStatus>('idle');
  const [stats, setStats] = useState<ExportStats | null>(null);
  const [fileSize, setFileSize] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleExport = async () => {
    setStatus('loading');
    setErrorMessage('');

    try {
      const result = await lancerExportDonnees();
      setStats(result.stats);
      setFileSize(result.fileSize);
      setStatus('success');
      telechargerExport(result.downloadUrl);
    } catch (error: any) {
      console.error('Erreur export :', error);
      if (error?.code === 'functions/resource-exhausted') {
        setStatus('rate-limited');
        setErrorMessage('Un seul export est autorisé par 24 heures. Réessayez demain.');
      } else {
        setStatus('error');
        setErrorMessage(error?.message || 'Une erreur est survenue lors de l\'export. Veuillez réessayer.');
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Exporter mes données</h1>

      {/* Bloc explicatif */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">
              Vos données vous appartiennent
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Conformément au RGPD (article 20 — droit à la portabilité), vous pouvez à tout moment
              télécharger une copie complète de vos données. L'export inclut vos documents, courriers,
              signalements, contacts, échéances et l'historique complet de vos échanges avec SENTINELLE.
            </p>
          </div>
        </div>
      </div>

      {/* Contenu du ZIP */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
          Contenu de l'export
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: FileText,      label: 'Documents GED',           desc: 'Fichiers originaux + métadonnées' },
            { icon: MailOpen,      label: 'Courriers entrants',       desc: 'JSON + CSV + pièces jointes' },
            { icon: Mail,          label: 'Courriers sortants',       desc: 'JSON + CSV + pièces jointes' },
            { icon: AlertTriangle, label: 'Signalements',             desc: 'JSON + CSV + photos' },
            { icon: File,          label: 'Dossiers',                 desc: 'Toutes vos affaires' },
            { icon: Users,         label: 'Contacts',                 desc: 'JSON + CSV' },
            { icon: CalendarDays,  label: 'Échéances',                desc: 'JSON + CSV' },
            { icon: MessageSquare, label: 'Conversations SENTINELLE', desc: 'Historique complet' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Icon className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-700">{label}</p>
                <p className="text-xs text-slate-500">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bouton d'export */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        {status === 'idle' && (
          <div className="text-center">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Exporter toutes mes données
            </button>
            <p className="text-xs text-slate-500 mt-3">
              Export disponible une fois par 24 heures — Format ZIP
            </p>
          </div>
        )}

        {status === 'loading' && (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
              <div>
                <p className="text-slate-700 font-medium">Export en cours...</p>
                <p className="text-sm text-slate-500">
                  Collecte de vos données et génération du fichier ZIP. Cela peut prendre quelques minutes.
                </p>
              </div>
            </div>
          </div>
        )}

        {status === 'success' && stats && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-50 rounded-full">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-slate-700 font-medium">Export terminé avec succès</p>
                <p className="text-sm text-slate-500">
                  Fichier ZIP — {formaterTailleFichier(fileSize)} — Le téléchargement devrait démarrer automatiquement.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-slate-600 mb-3">Données exportées</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {[
                  ['Documents',          stats.documents],
                  ['Courriers entrants', stats.courriersEntrants],
                  ['Courriers sortants', stats.courriersSortants],
                  ['Signalements',       stats.signalements],
                  ['Dossiers',           stats.dossiers],
                  ['Contacts',           stats.contacts],
                  ['Échéances',          stats.echeances],
                  ['Conversations',      stats.conversations],
                  ['Fichiers',           stats.fichiers],
                ].map(([label, val]) => (
                  <div key={label as string} className="flex justify-between">
                    <span className="text-slate-500">{label}</span>
                    <span className="font-medium text-slate-700">{val}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Le lien de téléchargement expire dans 1 heure. Prochain export possible dans 24h.</span>
            </div>
          </div>
        )}

        {status === 'rate-limited' && (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-50 rounded-full">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-slate-700 font-medium">{errorMessage}</p>
              <button onClick={() => setStatus('idle')} className="text-sm text-blue-600 hover:underline mt-1">
                Compris
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-full">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-slate-700 font-medium">{errorMessage}</p>
              <button onClick={() => setStatus('idle')} className="text-sm text-blue-600 hover:underline mt-1">
                Réessayer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Note RGPD */}
      <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <FileArchive className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-500 leading-relaxed">
          <p className="mb-1">
            <strong>Format de l'export :</strong> Archive ZIP contenant des fichiers JSON (données structurées),
            CSV (lisibles dans Excel) et les fichiers originaux (PDF, images).
          </p>
          <p>
            En cas de résiliation de votre abonnement, vous disposez de 30 jours pour exporter vos données.
            Passé ce délai, vos données seront définitivement supprimées de nos serveurs.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExportDonneesCSPage;
